import {useEffect, useState} from 'react'

// A section becomes active once its top edge crosses this fraction of the
// viewport height, measured from the top — right below the sticky bar.
const ACTIVE_LINE_VIEWPORT_RATIO = 0.2

const useActiveSectionScrollSpy = visibleSectionIds => {
  const [activeSectionId, setActiveSectionId] = useState(null)

  useEffect(() => {
    if (!visibleSectionIds?.length) return

    // Document-absolute offsets, cached. Reading a rect per section per frame forced a
    // synchronous layout on every scroll frame; section positions only move when something
    // reflows, so scrolling needs arithmetic against window.scrollY and nothing else.
    let sections = []
    let frameId = null

    const measure = () => {
      const {scrollY} = window

      sections = visibleSectionIds
        .map(id => document.getElementById(id))
        .filter(Boolean)
        .map(element => {
          const {top, bottom} = element.getBoundingClientRect()
          return {id: element.id, top: top + scrollY, bottom: bottom + scrollY}
        })
    }

    const update = () => {
      frameId = null

      if (!sections.length) return setActiveSectionId(null)

      const activeLine = window.scrollY + window.innerHeight * ACTIVE_LINE_VIEWPORT_RATIO
      const lastSection = sections[sections.length - 1]

      // Below every navigable section — related ads, news, report, financing
      // conditions… — none of them belongs to a tab, so nothing stays active.
      if (lastSection.bottom <= activeLine) return setActiveSectionId(null)

      // Last section whose top already crossed the line. When the line falls in
      // a gap between two sections this keeps the one above it active instead of
      // resetting to the first one.
      const crossedSections = sections.filter(({top}) => top <= activeLine)

      setActiveSectionId(crossedSections[crossedSections.length - 1]?.id ?? sections[0].id)
    }

    const scheduleUpdate = () => {
      if (frameId !== null) return
      frameId = window.requestAnimationFrame(update)
    }

    const remeasure = () => {
      measure()
      scheduleUpdate()
    }

    remeasure()

    // Anything that reflows the page invalidates the cache: a panel mounting late, the
    // description expanding, the financing calculator resizing, images settling. Observing the
    // body catches all of them, since each one changes the document height.
    const resizeObserver = new ResizeObserver(remeasure)
    resizeObserver.observe(document.body)

    window.addEventListener('scroll', scheduleUpdate, {passive: true})
    window.addEventListener('resize', remeasure)

    return () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId)
      resizeObserver.disconnect()
      window.removeEventListener('scroll', scheduleUpdate)
      window.removeEventListener('resize', remeasure)
    }
  }, [visibleSectionIds])

  return activeSectionId
}

export default useActiveSectionScrollSpy
